/**
 * src/hooks/useCaptcha.ts
 * =============================================================================
 * Empieza a resolver el captcha en cuanto se abre el formulario, para que al
 * pulsar "Crear cuenta" ya esté listo y el usuario no espere nada.
 *
 * `obtener()` devuelve la solución preparada (o espera a que termine) y
 * arranca enseguida la siguiente: cada solución sirve una sola vez, y si el
 * envío falla (contraseña corta, correo repetido…) el segundo intento no
 * debe esperar de nuevo. Si una resolución falla (red caída), `obtener()`
 * vuelve a intentarlo en ese momento.
 * =============================================================================
 */

import { useCallback, useEffect, useRef } from 'react'
import { resolverCaptcha } from '@/lib/captcha'

export function useCaptcha(): () => Promise<string> {
  const pendiente = useRef<Promise<string> | null>(null)

  const preparar = useCallback(() => {
    const p = resolverCaptcha()
    // Evita el aviso de promesa rechazada sin manejar; el error real lo verá
    // quien llame a `obtener()`.
    p.catch(() => undefined)
    pendiente.current = p
  }, [])

  useEffect(() => {
    preparar()
  }, [preparar])

  return useCallback(async () => {
    const actual = pendiente.current ?? resolverCaptcha()
    preparar()
    try {
      return await actual
    } catch {
      return resolverCaptcha()
    }
  }, [preparar])
}
